import { debounce } from '@direct-frontend/stdlib';
import { createForm, type ValidationError } from '@adv-frontend/form-kit';
import { proceedOrderPayment, updateDigitalOrder } from '@/entities/digitalOrder';
import { InvalidOrderError, OrderInvalidEmailError, OrderInvalidPhoneError, OrderPaymentError } from '@/entities/order';
import { trackGoal } from '@/entities/trackGoal';
// eslint-disable-next-line @direct-frontend/modules/slice-boundaries -- Уберем форму клинта в фичу ордер формы
import { CLIENT_FORM_ID, clientForm } from '@/features/ClientPicker';
import { type SchemaDigitalOrder } from '@/shared/api/openApi';
import { modalsService } from '@/shared/lib/modalsService';
import { fromDigitalFormValues } from '../../lib/mappers/fromDifitalFormValues';
import { type ClientFieldValue, fieldClient } from './fieldClient';

type DigitalOrderFormContext = {
    orderId: string;
    signature: string;
    storeSlug: string;
    storeUrl?: string;
    order: SchemaDigitalOrder;
    refetch: VoidFunction;
};

export type DigitalOrderFormValues = {
    client: ClientFieldValue;
};

const DEBOUNCE_TIMEOUT = 300;

const updateOrderDebounced = debounce(updateDigitalOrder, DEBOUNCE_TIMEOUT);

export const digitalOrderForm = createForm({
    name: 'DigitalOrderForm',
    fields: {
        client: fieldClient,
    },

    context: (formContext: DigitalOrderFormContext) => {
        return { form: formContext };
    },

    validators: (createValidators) =>
        createValidators<ValidationError<'REQUIRED_CLIENT' | 'PAYMENT_FAILED'>>({
            onChange: [
                async (values, formApi) => {
                    const instance = digitalOrderForm.getInstance(formApi.id);
                    const { orderId, signature, storeSlug } = instance.getContext();
                    const orderMeta = fromDigitalFormValues(orderId, values);

                    if (!orderMeta || !instance.getState().isDirty) {
                        return;
                    }

                    await updateOrderDebounced({
                        orderMeta,
                        signature,
                        storeSlug,
                    });
                },
            ],
            onSubmit: [
                (values, formApi) => {
                    trackGoal('proceed_payment');

                    if (
                        !values.client.firstName ||
                        !values.client.lastName ||
                        !values.client.phone ||
                        !values.client.email
                    ) {
                        formApi.fields.client.addValidationError({ code: 'REQUIRED_CLIENT' });
                    }
                },
            ],
        }),

    onSubmit: async (values, formApi) => {
        const context = formApi.getContext();
        const serialized = fromDigitalFormValues(context.orderId, values);
        const clientFormInstance = clientForm.getInstance(CLIENT_FORM_ID);

        if (!serialized) {
            return;
        }

        try {
            await proceedOrderPayment(serialized, context.signature, context.storeSlug, context.storeUrl);
            context.refetch();

            return;
        } catch (error) {
            trackGoal('payment_error');

            if (error instanceof OrderInvalidEmailError) {
                formApi.fields.client.addValidationError({ code: 'INVALID_EMAIL_FORMAT' });
                clientFormInstance.fields.email.addValidationError({ code: 'INVALID_EMAIL_FORMAT' });
                modalsService.onInvalidEmail();

                return;
            }

            if (error instanceof OrderInvalidPhoneError) {
                formApi.fields.client.addValidationError({ code: 'INVALID_PHONE_FORMAT' });
                clientFormInstance.fields.phone.addValidationError({ code: 'INVALID_PHONE_FORMAT' });
                modalsService.onInvalidPhone();

                return;
            }

            if (error instanceof OrderPaymentError) {
                formApi.addValidationError({ code: 'PAYMENT_FAILED' });
            }

            const errorDescription = error instanceof InvalidOrderError ? error.errorDescription : undefined;

            modalsService.onSubmissionError(errorDescription);

            throw error;
        }
    },
});
